import { useState } from 'react';

import { get, post } from 'helpers/api';
import { useOneOffSubscription } from 'helpers/action_cable/use_one_off_subscription';

import type { AssistantToolCall } from './tools/types';
import type { Agent, Message, Paths, StreamFrame } from './types';

interface AskResult {
  toolCalls?: AssistantToolCall[];
  error?: boolean;
}

const isTerminal = (frame: StreamFrame) => frame.type === 'done' || frame.type === 'error';

export function useAssistant(agents: Agent[] | null | undefined, paths: Paths) {
  const { subscribe } = useOneOffSubscription();

  const [agent, setAgent] = useState((agents && agents[0]) || null);
  const [model, setModel] = useState(agent?.models.find(({ id }) => id === agent.model) || null);
  const [chatId, setChatId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  async function loadChat(): Promise<{ chatId: number; messages: Message[] } | null> {
    const response = await get(paths.chat);
    if (!response?.chatId) return null;

    return { chatId: response.chatId, messages: response.messages ?? [] };
  }

  async function ask({ attachments, prompt, onFrame }: {
    attachments?: File[],
    prompt: string,
    onFrame: (frame: StreamFrame) => void
  }) {
    function buildAskFormData(requestId: string) {
      const form = new FormData();

      form.append('prompt', prompt);
      form.append('request_id', requestId);
      attachments?.forEach(attachment => form.append('attachments[]', attachment));

      if (agent?.name) {
        form.append('agent', agent.name);
      }

      if (chatId != null) {
        form.append('chat_id', String(chatId));
      }

      if (model?.id) {
        form.append('model', model.id);
      }

      return form;
    }

    return new Promise<AskResult>((resolve) => {
      setLoading(true);
      function finish(result: AskResult) {
        setLoading(false);
        resolve(result);
      }

      subscribe<StreamFrame>(
        {
          async connected(requestId: string) {
            const response = await post(paths.ask, buildAskFormData(requestId));
            setChatId(response.chatId);
          },
          connectedError() {
            onFrame({ type: 'error', error: true });
            finish({ error: true });
          },
          received(frame) {
            onFrame(frame);
            if (frame.type === 'done') finish({ toolCalls: frame.toolCalls });
            else if (frame.type === 'error') finish({ error: true });
          },
        },
        { isTerminal }
      );
    });
  }

  return {
    agent,
    ask,
    loadChat,
    loading,
    model,
    setAgent,
    setChatId,
    setModel,
  };
}
